import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, FileText, LayoutGrid, Star } from "lucide-react";
import { BIZ } from "@/data/site";

/**
 * Mobile-only sticky bottom app-bar with 4 one-tap thumb-reach CTAs:
 *   Call · Quote · Services · Reviews
 * Hidden on md+ (where the top nav already shows these links).
 *
 * Rendered globally in App.js so every route gets the bar. Honours iOS safe-area
 * so the icons never sit under the home indicator.
 */
export default function MobileActionBar() {
  const { pathname, hash } = useLocation();

  const track = (name) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", name, { source: "mobile_action_bar" });
    }
  };

  // Highlight the active anchor section when on the landing page.
  const isActive = (frag) => pathname === "/" && hash === frag;

  const items = [
    {
      type: "a",
      href: `tel:${BIZ.phoneTel}`,
      onClick: () => track("click_to_call"),
      icon: Phone,
      label: "Call",
      testId: "mab-call",
      accent: true,
    },
    {
      type: "link",
      to: "/#quote",
      onClick: () => track("mab_quote"),
      icon: FileText,
      label: "Quote",
      testId: "mab-quote",
      active: isActive("#quote"),
    },
    {
      type: "link",
      to: "/#services",
      onClick: () => track("mab_services"),
      icon: LayoutGrid,
      label: "Services",
      testId: "mab-services",
      active: isActive("#services"),
    },
    {
      type: "link",
      to: "/#reviews",
      onClick: () => track("mab_reviews"),
      icon: Star,
      label: "Reviews",
      testId: "mab-reviews",
      active: isActive("#reviews"),
    },
  ];

  return (
    <div
      data-testid="mobile-action-bar"
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-stone-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <nav className="grid grid-cols-4 gap-0.5 px-1 pt-1.5 pb-1" aria-label="Primary mobile actions">
        {items.map((it) => {
          const Icon = it.icon;
          const base =
            "flex flex-col items-center justify-center gap-0.5 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wide transition-colors active:scale-[0.96]";
          const tone = it.accent
            ? "text-white bg-brand-600 hover:bg-brand-700 shadow-md shadow-brand-600/30"
            : it.active
            ? "text-brand-700 bg-brand-50"
            : "text-stone-700 hover:bg-stone-100";
          const content = (
            <>
              <Icon className="w-5 h-5" strokeWidth={2.2} />
              <span className="leading-none">{it.label}</span>
            </>
          );
          if (it.type === "a") {
            return (
              <a key={it.testId} href={it.href} onClick={it.onClick} data-testid={it.testId} className={`${base} ${tone}`}>
                {content}
              </a>
            );
          }
          return (
            <Link key={it.testId} to={it.to} onClick={it.onClick} data-testid={it.testId} className={`${base} ${tone}`}>
              {content}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
